import { normalizeSegmentId, normalizeSegmentIds } from '@/helper/personalization/normalizeSegmentId';
import type {
  ComponentPersonalizedContentStoryblok,
  ComponentRichTextStoryblok,
  ComponentTeaserStoryblok,
} from '@/types/component-types-sb';

export type PersonalizableNestedBlok = ComponentTeaserStoryblok | ComponentRichTextStoryblok;

export type ResolvePersonalizedBlocksResult = {
  blocks: PersonalizableNestedBlok[];
  isPersonalized: boolean;
  matchedSegmentContentUid?: string;
};

function segmentContentMatchesUserSegments(
  segmentTargets: (number | string)[] | undefined,
  userSegmentIdSet: Set<string>
): boolean {
  if (!segmentTargets?.length || userSegmentIdSet.size === 0) {
    return false;
  }
  return segmentTargets.some((target) => userSegmentIdSet.has(normalizeSegmentId(target)));
}

export function resolvePersonalizedBlocks(
  blok: ComponentPersonalizedContentStoryblok,
  userSegmentIds: string[]
): ResolvePersonalizedBlocksResult {
  const defaultBlocks = blok.default_content ?? [];
  const normalizedUserIds = normalizeSegmentIds(userSegmentIds);

  if (normalizedUserIds.length === 0) {
    return { blocks: defaultBlocks, isPersonalized: false };
  }

  const userSegmentIdSet = new Set(normalizedUserIds);
  const segmentContentList = blok.segment_content ?? [];

  for (const segmentEntry of segmentContentList) {
    if (!segmentContentMatchesUserSegments(segmentEntry.segment, userSegmentIdSet)) {
      continue;
    }

    const personalizedBlocks = segmentEntry.personalized_content ?? [];
    if (personalizedBlocks.length === 0) {
      continue;
    }

    return {
      blocks: personalizedBlocks,
      isPersonalized: true,
      matchedSegmentContentUid: segmentEntry._uid,
    };
  }

  return { blocks: defaultBlocks, isPersonalized: false };
}
